import { WorkspaceContext } from "@/services/messageService";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface ConversationFiltersState {
  workspaceId: string | undefined;
  setWorkspaceId: (workspaceId: string | undefined) => void;
  filters: Record<string, Array<Record<string, unknown>>>;
  setFilters: (
    source: string,
    filter: Record<string, unknown>,
    indexToUpdate?: number
  ) => void;
  removeFilter: (source: string, filterIndex: number) => void;

  selectedSource: string | undefined;
  setSelectedSource: (source: string | undefined) => void;
  resetFilters: () => void;

  workspaceContext: WorkspaceContext;
  setWorkspaceContext: (context: WorkspaceContext) => void;
  resetWorkspaceContext: () => void;
}

export const useNewConversationFiltersStore = create(
  persist<ConversationFiltersState>(
    (set) => ({
      selectedSource: undefined,
      workspaceContext: {},
      workspaceId: undefined,
      filters: {},
      // Add or update filters for a specific source
      setFilters: (source, filter, indexToUpdate?: number) => {
        set((state) => {
          const existingFilters = state.filters[source] || [];

          // If an index is provided, update the existing filter at that index
          let updatedFilters;
          if (
            indexToUpdate !== undefined &&
            indexToUpdate >= 0 &&
            indexToUpdate < existingFilters.length
          ) {
            updatedFilters = existingFilters.map((existingFilter, index) =>
              index === indexToUpdate ? filter : existingFilter
            );
          } else {
            // If no index is provided, add the filter as a new group
            updatedFilters = [...existingFilters, filter];
          }

          return {
            filters: {
              ...state.filters,
              [source]: updatedFilters,
            },
          };
        });
      },
      // Remove a specific filter by index for a given source
      removeFilter: (source, filterIndex) =>
        set((state) => {
          const updatedFilters = { ...state.filters };
          if (updatedFilters[source]) {
            updatedFilters[source] = updatedFilters[source].filter(
              (_, index) => index !== filterIndex
            );

            // Remove the source key if no filters remain
            if (updatedFilters[source].length === 0) {
              delete updatedFilters[source];
            }
          }
          return { filters: updatedFilters };
        }),
      setWorkspaceId: (workspaceId) => set({ workspaceId }),
      setSelectedSource: (source) => set({ selectedSource: source }),
      setWorkspaceContext: (context) => set({ workspaceContext: context }),
      resetFilters: () => set({ selectedSource: undefined, filters: {} }),
      resetWorkspaceContext: () => set({ workspaceContext: {} }),
    }),
    {
      name: "new-conversation-filters",
    }
  )
);
